import { startOfDay } from 'date-fns';
import { find, isEmpty } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import pluralize from 'pluralize';
import type { ReactElement } from 'react';

import { AccordionBlock } from '@/blocks/accordion-block';
import { GridBlock } from '@/blocks/grid-block';
import { HeaderBlock } from '@/blocks/header-block';
import { SimpleScheduleBlock } from '@/blocks/schedule-block';
import { ScheduleSearchBlock } from '@/blocks/schedule-search-block';
import { TableBlock } from '@/blocks/table-block';
import { MiniChartCard } from '@/components/cards/mini-chart-card';
import {
  formatNumbersForChart,
  getLastTwoMonthsDataSummary,
  getLastXMonths,
  NUMBER_VALUE_FORMATS,
} from '@/components/cards/mini-chart-card/utils';
import { CarrierSummary } from '@/components/carrier-summary';
import { Heading } from '@/components/heading';
import { PageLayout } from '@/components/layouts/page-layout';
import { RankingList } from '@/components/modules/ranking-list';
import { ShipmentLiteTrackingForm } from '@/components/modules/shipment/lite-tracking-form';
import { PageLoading } from '@/components/page-loading';
import { Text } from '@/components/text';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { PAGE_REVALIDATE_INTERVAL_MONTH, RELIABILITY_CHART_KEYS, RESPONSIVE_IMAGE_WORLD_MAP } from '@/lib/constants';
import { FEATURE_FLAGS } from '@/lib/constants/feature-flags';
import { colors } from '@/theme/constants';
import { CarrierPageInterface } from '@/types';
import { ScheduleNextDeparturesApiResponse } from '@/types/api-response-types';
import { IdentifierApiType, ReliabilityApiInterface } from '@/types/api-types';
import { PostSnippets } from '@/types/cms-types';
import { carrierService } from '@/utils/api/services/carriers.service';
import { cmsPostService } from '@/utils/api/services/cms.service/post';
import { scheduleService } from '@/utils/api/services/schedules.service';
import { getCarrierLongName } from '@/utils/carrier';
import { formatCarrierDescription, formatCarrierTitle } from '@/utils/format/carrier-description';
import { generateCarrierFAQs } from '@/utils/frequently-asked-questions';
import {
  createCarrierPageUrl,
  createMetaTagsWithCustomTitleAndDescription,
  getPreferredIdentifier,
  isProductionEnv,
} from '@/utils/helpers';
import { getOrdinalSuffix } from '@/utils/helpers/get-ordinal-suffix';

const IS_PROD = isProductionEnv();

interface PageProps {
  carrier: CarrierPageInterface;
  schedules?: ScheduleNextDeparturesApiResponse | null;
  posts: PostSnippets;
}

const Page = ({ carrier, schedules, posts }: PageProps): ReactElement => {
  const router = useRouter();
  const { variant: SHOW_SCHEDULE_SEARCH } = useFeatureFlag(FEATURE_FLAGS.STANDALONE_SCHEDULE_SEARCH);

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <PageLoading />;
  }

  // Bit of a hack to get the carrier longname
  const carrierLongName = schedules ? getCarrierLongName(Object.values(schedules?.carriers)[0]) : '';
  const shouldShowLongName = carrierLongName && carrierLongName !== carrier.name;

  const carrierPreferredId = getPreferredIdentifier(carrier.identifiers)?.value || '';

  // Define default search form props
  const firstScheduledRoute = schedules?.routes[0];

  const searchBoxParams = {
    origin: firstScheduledRoute?.from,
    destination: firstScheduledRoute?.to,
    carrierId: firstScheduledRoute?.carrierId,
    carrier: firstScheduledRoute && schedules?.carriers && schedules.carriers[firstScheduledRoute?.carrierId || ''],
    departDate: firstScheduledRoute && startOfDay(new Date(firstScheduledRoute?.departureDateTime)),
  };

  const { hasIncreased, latestMonthText, secondToLastValue, lastValue, difference } = getLastTwoMonthsDataSummary({
    data: carrier.reliability,
    key: RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY,
  });

  const {
    hasIncreased: averageDaysHasIncreased,
    latestMonthText: averageDaysLatestMonthText,
    secondToLastValue: averageDaysSecondToLastValue,
    lastValue: averageDaysLastValue,
    difference: averageDaysDifference,
  } = getLastTwoMonthsDataSummary({
    data: carrier.reliability,
    key: RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE,
    dataType: NUMBER_VALUE_FORMATS.WHOLE_NUMBER,
    decimalPlaces: 1,
  });

  const transformedArrivalOnTimePerformance = formatNumbersForChart({
    data: carrier.reliability,
    key: RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY,
    multiplier: 100,
  });

  const transformedAverageDaysLate = formatNumbersForChart({
    data: carrier.reliability,
    key: RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE,
  });

  return (
    <PageLayout metaTags={carrier.seoMetaTags} canonical={carrier.canonical} headerVariant="dark" posts={posts}>
      <div className="blocks">
        <HeaderBlock id="page-header" variant="route" image={RESPONSIVE_IMAGE_WORLD_MAP}>
          <div className="grid items-center gap-8 lg:grid-cols-[3fr_2fr]">
            <div>
              <Heading variant="d2" as="h1" className="mb-0 mt-4 font-bold">
                {carrier.name}
              </Heading>
              {shouldShowLongName && <Text className="font-medium">{carrierLongName}</Text>}
              <div className="my-6 max-w-xl">
                <Text>{carrier.description}</Text>
              </div>
              <CarrierSummary carrier={carrier} />
            </div>
            {Boolean(carrier.tracking) && (
              <div className="pt-6 lg:flex lg:justify-end">
                <ShipmentLiteTrackingForm
                  carrierId={carrierPreferredId}
                  carrierName={carrier.name}
                  carrierTrackingId={carrier.tracking?.[0].name}
                  type={carrier.tracking?.[0].type}
                />
              </div>
            )}
          </div>
        </HeaderBlock>
        {schedules && (
          <>
            <SimpleScheduleBlock
              id="carrier-schedules"
              carrierName={carrier.name}
              carrierLongName={shouldShowLongName ? carrierLongName : ''}
              schedules={schedules}
            />
            {(!IS_PROD || SHOW_SCHEDULE_SEARCH) && (
              <ScheduleSearchBlock
                id="schedule-search"
                mode={carrier.mode}
                searchProps={searchBoxParams}
                isUnderPageHeader
              />
            )}
          </>
        )}
        {!isEmpty(carrier.ports) && (
          <TableBlock
            id="port-carriers"
            heading="Top 10 ports serviced by this carrier"
            body={`<p>These are the top 10 ports that ${carrier.name} services. They are ranked by frequency of departures and include both international and domestic services.</p>`}
            rows={carrier.ports}
            loadMore={10}
            columns={[
              { key: 'rank', name: 'Rank' },
              { key: 'portCode', name: 'Port Code' },
              { key: 'name', name: 'Port Name' },
              { key: 'countryName', name: 'Country' },
              { key: 'frequency', name: 'Departure frequency' },
              { key: 'link', name: '' },
            ]}
          />
        )}
        <GridBlock id="ranking-list" heading="Carrier statistics" columns="three" noTopPadding>
          {!isEmpty(carrier.globalRankings) && (
            <RankingList
              title="Global Rank"
              description={`${carrier.name} is ranked ${getOrdinalSuffix(find(carrier.globalRankings, { id: carrier.id })?.ranking || 0)} in the world against other carriers when comparing service frequency and global port coverage.`}
              data={{ globalRankings: carrier.globalRankings }}
              highlightedItemRank={find(carrier.globalRankings, { id: carrier.id })?.ranking || 0}
              highlightedItemId={carrier.nodeId || ''}
              maxListLength={5}
            />
          )}
          {!isEmpty(carrier.reliability) && (
            <>
              <MiniChartCard
                data={transformedArrivalOnTimePerformance}
                id="carrier-otp"
                heading="On time performance"
                body={`This chart displays the On Time Arrival performance for ${carrier.name} as measured by comparing all vessel scheduled vessel arrivals vs actual arrivals.`}
                chartHeading={`${latestMonthText} OTP`}
                chartHeadingHighlight={`${lastValue}%`}
                chartSubHeading={`Previous month OTP ${secondToLastValue}%`}
                badgeText={`${difference}%`}
                badgeType={hasIncreased ? 'positive' : 'negative'}
                isLocked={false}
                chartConfig={{
                  [RELIABILITY_CHART_KEYS.SCHEDULE_RELIABILITY]: {
                    label: 'Schedule Reliability',
                    color: colors.charts.line[0],
                  },
                }}
                chartValueSuffix="%"
              />
              <MiniChartCard
                data={transformedAverageDaysLate}
                id="carrier-average-days-late"
                heading="Average days late"
                body={`A vessel is considered late when arriving more than 1 day behind schedule. This chart shows the average number of days that ${carrier.name} vessels are typically late by.`}
                chartHeading={averageDaysLatestMonthText}
                chartHeadingHighlight={`${averageDaysLastValue} ${pluralize('day', Number(averageDaysLastValue))}`}
                chartSubHeading={`Previous month ${averageDaysSecondToLastValue} ${pluralize('day', Number(averageDaysSecondToLastValue))}`}
                badgeText={`${averageDaysDifference} ${pluralize('day', Number(averageDaysDifference))}`}
                badgeType={averageDaysHasIncreased ? 'negative' : 'positive'}
                badgeArrowDirection={averageDaysHasIncreased ? 'up' : 'down'}
                isLocked={false}
                chartConfig={{
                  [RELIABILITY_CHART_KEYS.AVERAGE_DAYS_LATE]: {
                    label: 'Average days late',
                    color: colors.charts.line[0],
                  },
                }}
              />
            </>
          )}
        </GridBlock>
        <AccordionBlock
          id="faqs"
          heading={`Frequently asked questions about ${carrier.name}`}
          items={carrier.faqs}
          className="bg-grey-100"
        />
      </div>
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params = {} }) => {
  const { slug } = params;

  // Extract id and type from slug
  // @TODO: convert to testable function
  const prependedSlug = slug?.length === 1 ? ['canonical', ...slug] : slug;
  const [carrierCodeType, carrierId] = prependedSlug || [];

  const [carrierData, posts] = await Promise.all([
    carrierService.getOne(carrierId, carrierCodeType as IdentifierApiType),
    cmsPostService.getPostSnippetsCached(),
  ]);

  // Return not found if no carrier details
  if (!carrierData || !carrierData.name || isEmpty(carrierData.ports)) {
    return {
      notFound: true,
    };
  }

  // SEO - Generate page titles, descriptions etc
  const canonical = createCarrierPageUrl(carrierData.identifiers, true);
  const description = formatCarrierDescription(carrierData);
  const seoMetaTags = createMetaTagsWithCustomTitleAndDescription({
    title: formatCarrierTitle(carrierData),
    description,
  });

  // Get schedules
  const carrierSchedules = await scheduleService.nextDepartures({
    mode: carrierData.mode,
    carriers: [carrierData.carrierId],
    departurePorts: carrierData.portIds,
  });

  // Generate FAQs for page
  const faqs = carrierData ? generateCarrierFAQs(carrierData) : undefined;

  const lastSixMonthsReliabilityData = getLastXMonths({
    data: carrierData.reliability,
    months: 6,
  }) as ReliabilityApiInterface[];

  return {
    props: {
      carrier: {
        ...carrierData,
        reliability: lastSixMonthsReliabilityData,
        description,
        canonical,
        seoMetaTags,
        faqs,
      },
      schedules: carrierSchedules,
      posts,
    },
    revalidate: PAGE_REVALIDATE_INTERVAL_MONTH,
  };
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: true,
});

export default Page;
